import { Button, FormSection, HelpList, More } from "@omnabase/ui";
import { CUSTOMER_LINKS_MAX, CustomerLinksSchema, customerLinkProblem, type CustomerLink, type Project } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { api } from "../../lib/api";
import { useFormState } from "../../lib/save";

/** The customer sidebar round (2026-11-01, brief rule 4 and its "After the red team's review"): Projects › General's last section. An
 *  administrator lists up to five named links to the team's own tools (a shop's customer page, a CRM) with the customer's email, name or id
 *  filled in; they show as small buttons beside every ticket of the project and on the customer page. The list is saved whole with Save
 *  (`PATCH /projects/:id { customerLinks }`), checked by the shared rule first so a refusal names the row. Here, not under Integrations:
 *  those are data connections, these are buttons. */
const EMPTY: CustomerLink = { label: "", url: "" };
const PLACEHOLDERS = ["{email}: the customer's email address", "{name}: the customer's name", "{id}: the customer's id in OmnaBase"];

export function ProjectCustomerLinks({ project: p }: { project: Project }) {
  const { workspace, reload, toast } = useSession();
  const saved = p.customerLinks ?? [];
  const [links, setLinks] = useFormState<CustomerLink[]>(saved, p.id);
  const change = (i: number, patch: Partial<CustomerLink>) => setLinks((cur) => cur.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  const dirty = JSON.stringify(links) !== JSON.stringify(saved);
  const save = async () => {
    const checked = CustomerLinksSchema.safeParse(links.map((l) => ({ label: l.label.trim(), url: l.url.trim() })));
    if (!checked.success) { toast(problemWords(checked.error.issues[0]), { kind: "error" }); return; }
    try { await api.patch(`/api/v1/w/${workspace.slug}/projects/${p.id}`, { customerLinks: checked.data }); await reload(); toast("Customer links saved", { kind: "success" }); }
    catch (e) { toast((e as Error).message, { kind: "error" }); }
  };
  return (
    <FormSection title="Customer links in the ticket sidebar" intro={<>Buttons beside every ticket of this project and on the customer page that open your own tools for that customer.<More label="Placeholders"><HelpList items={PLACEHOLDERS} /></More></>}>
      <div className="ob-customer-links">
        {links.map((l, i) => <LinkRow key={i} link={l} onChange={(patch) => change(i, patch)} onRemove={() => setLinks((cur) => cur.filter((_, n) => n !== i))} />)}
        <div className="ob-customer-links__foot">
          <Button size="sm" icon="plus" disabled={links.length >= CUSTOMER_LINKS_MAX} onClick={() => setLinks((cur) => [...cur, EMPTY])}>Add link</Button>
          <Button size="sm" variant="primary" disabled={!dirty} onClick={() => void save()}>Save</Button>
        </div>
      </div>
    </FormSection>
  );
}

/** one link: its name, its address with the problem under it once typed, and its remove button */
function LinkRow({ link, onChange, onRemove }: { link: CustomerLink; onChange: (patch: Partial<CustomerLink>) => void; onRemove: () => void }) {
  const problem = link.url.trim() ? customerLinkProblem(link.url.trim()) : null;
  return (
    <div className="ob-customer-links__row">
      <input className="ob-dialog__input ob-customer-links__label" value={link.label} maxLength={30} placeholder="Shop customer" aria-label="Link label" onChange={(e) => onChange({ label: e.target.value })} />
      <input className="ob-dialog__input ob-customer-links__url" value={link.url} maxLength={500} placeholder="https://example.com/admin?email={email}" aria-label="Link address" aria-invalid={problem ? true : undefined} onChange={(e) => onChange({ url: e.target.value })} />
      <Button variant="icon" size="sm" icon="x" aria-label={`Remove link ${link.label || "without a name"}`} onClick={onRemove} />
      {problem && <span className="ob-customer-links__problem" role="alert">Needs {problem}</span>}
    </div>
  );
}

/** the first refusal of the list in words naming the row ("Link 2 needs an https:// address") */
function problemWords(issue: { path: PropertyKey[]; message: string } | undefined): string {
  if (!issue) return "Check the links";
  const [row, field] = issue.path;
  if (typeof row !== "number") return `At most ${CUSTOMER_LINKS_MAX} links`;
  const what = field === "url" ? issue.message : /own name/.test(issue.message) ? "its own name" : "a name of 1 to 30 characters";
  return `Link ${row + 1} needs ${what}`;
}
